import { canonicalStringify } from '@/core/util/canonical';
import { migrate, SaveFromFutureError } from './migrations';
import { zGameSaveV1, type GameSave } from './schema/v1';

/** Largest save file we try to read (content-schemas §11.7); real saves are a few dozen KB. */
export const SAVE_FILE_MAX_BYTES = 2 * 1024 * 1024;
const FORMAT = 'lct-save';

export interface SaveSummary {
  coins: number;
  stars: number;
  items: number;
}

export type SaveFileResult =
  | { ok: true; save: GameSave; summary: SaveSummary }
  | { ok: false; reason: 'too_big' | 'invalid' | 'future' };

/** Export wrapper `{ format: 'lct-save', exportedAt, appVersion, save }` as pretty JSON (content-schemas §11.7). */
export function encodeSaveFile(save: GameSave, exportedAt: number, appVersion: string): string {
  const body = JSON.parse(canonicalStringify(save)) as unknown;
  return JSON.stringify({ format: FORMAT, exportedAt, appVersion, save: body }, null, 2);
}

/**
 * Reads an exported file without touching anything: size check, JSON, wrapper format, migration, schema and
 * invariants. Bare saves (files exported before the wrapper existed) are accepted too.
 */
export function decodeSaveFile(text: string): SaveFileResult {
  if (new TextEncoder().encode(text).length > SAVE_FILE_MAX_BYTES) return { ok: false, reason: 'too_big' };
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return { ok: false, reason: 'invalid' };
  }
  if (isRecord(raw) && 'format' in raw) {
    if (raw.format !== FORMAT) return { ok: false, reason: 'invalid' };
    raw = raw.save;
  }
  let migrated: unknown;
  try {
    migrated = migrate(raw);
  } catch (e) {
    return { ok: false, reason: e instanceof SaveFromFutureError ? 'future' : 'invalid' };
  }
  const parsed = zGameSaveV1.safeParse(migrated);
  if (!parsed.success) return { ok: false, reason: 'invalid' };
  return { ok: true, save: parsed.data, summary: summarizeSave(parsed.data) };
}

/** What the import confirmation shows: coins, stars and how many things the player owns. */
export function summarizeSave(save: GameSave): SaveSummary {
  const products = Object.values(save.inventory.productStacks).reduce((n, q) => n + q, 0);
  return {
    coins: save.wallet.coins,
    stars: save.stars.total,
    items: Object.keys(save.inventory.instances).length + products,
  };
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}
